import { Box, Stack, Typography } from "@mui/material";
import { InfoWindowF } from "@react-google-maps/api";
import { types, utils } from "@vilocnv/allsetra-core";
import { Clock, MileageIcon } from "assets/icons";
import { IObjectRideSegment } from "app/data/types";
import {
  getSegmentColor,
  getSegmentDistanceKm,
  getSegmentDurationMinutes,
  getSegmentEndpoints,
  MixedRideSegmentEndpoint,
  MIXED_RIDE_SEGMENT_COLORS,
  normalizeSegmentMode,
  sliceLocationsForSegment
} from "app/data/helpers/mixedRideSegmentHelpers";
import {
  IObjectRideWithSegments,
  ObjectRideModeEnum
} from "app/data/helpers/objectRideHelpers";
import { getResolvedAddressFromStore } from "app/data/helpers/addressValidationHelpers";
import { useTranslation } from "react-i18next";

const formatPopupAddress = (address: string) => {
  if (!address) {
    return { line1: "", line2: "" };
  }

  const dutch = address.match(
    /^(.+?),\s*(\d{4}\s*[A-Z]{2})\s+([^,]+?)(?:,\s*[^,]+)?$/i
  );
  if (dutch) {
    return {
      line1: `${dutch[1].trim()}, ${dutch[2].trim()}`,
      line2: dutch[3].trim()
    };
  }

  const [street, ...rest] = address.split(",");
  return {
    line1: street?.trim() || address,
    line2: rest.join(",").trim()
  };
};

const SplitDot = () => (
  <Box
    width={14}
    height={14}
    minHeight={14}
    borderRadius="50%"
    overflow="hidden"
    position="relative"
    flexShrink={0}
    bgcolor="#FFFFFF"
    zIndex={1}
  >
    <Box
      position="absolute"
      left={0}
      top={0}
      width="50%"
      height="100%"
      bgcolor={MIXED_RIDE_SEGMENT_COLORS.business}
    />
    <Box
      position="absolute"
      right={0}
      top={0}
      width="50%"
      height="100%"
      bgcolor={MIXED_RIDE_SEGMENT_COLORS.private}
    />
    <Box
      position="absolute"
      left="3.5px"
      top="3.5px"
      width={7}
      height={7}
      borderRadius="50%"
      bgcolor="#FFFFFF"
    />
  </Box>
);

const EndpointCopy = ({ endpoint }: { endpoint: MixedRideSegmentEndpoint }) => {
  const parts = formatPopupAddress(endpoint.address);
  const time = endpoint.date
    ? utils.formatDate({ date: endpoint.date, dateFormat: "HH:mm" })
    : "";

  return (
    <Box
      display="grid"
      gridTemplateColumns="minmax(0, 1fr) auto"
      columnGap={2}
      alignItems="start"
      minWidth={0}
    >
      <Box minWidth={0}>
        <Typography
          fontSize={13}
          fontWeight={600}
          color="#1D1F2B"
          lineHeight={1.3}
          noWrap
        >
          {parts.line1 || endpoint.address}
        </Typography>
        {parts.line2 ? (
          <Typography fontSize={13} color="#1D1F2B" lineHeight={1.3} noWrap>
            {parts.line2}
          </Typography>
        ) : null}
      </Box>
      <Typography
        fontSize={12}
        color="#76828F"
        lineHeight={1.3}
        whiteSpace="nowrap"
        pt="1px"
      >
        {time}
      </Typography>
    </Box>
  );
};

interface Props {
  ride: IObjectRideWithSegments;
  segment: IObjectRideSegment;
  locations: Array<types.IObjectRideLocation>;
  resolvedAddresses?: any[];
  onClose: () => void;
}

const MixedRideSegmentPopup = ({
  ride,
  segment,
  locations,
  resolvedAddresses = [],
  onClose
}: Props) => {
  const { t } = useTranslation();
  const endpoints = getSegmentEndpoints(ride, segment, locations);
  if (!endpoints) return null;

  const color = getSegmentColor(segment.mode);
  const isPrivate =
    normalizeSegmentMode(segment.mode) === ObjectRideModeEnum.PRIVATE;
  const startAddress =
    getResolvedAddressFromStore(
      endpoints.start.address,
      resolvedAddresses,
      endpoints.start.latitude,
      endpoints.start.longitude
    ) || endpoints.start.address;
  const endAddress =
    getResolvedAddressFromStore(
      endpoints.end.address,
      resolvedAddresses,
      endpoints.end.latitude,
      endpoints.end.longitude
    ) || endpoints.end.address;

  const start = { ...endpoints.start, address: startAddress || "" };
  const end = { ...endpoints.end, address: endAddress || "" };
  const path = sliceLocationsForSegment(locations, segment);
  const midPoint = path[Math.floor(path.length / 2)];
  const position = {
    lat: midPoint?.latitude ?? (start.latitude + end.latitude) / 2,
    lng: midPoint?.longitude ?? (start.longitude + end.longitude) / 2
  };

  return (
    <InfoWindowF
      position={position}
      onCloseClick={onClose}
      options={
        {
          maxWidth: 340,
          minWidth: 280,
          pixelOffset: new google.maps.Size(0, -80),
          disableAutoPan: false,
          // Supported at runtime; missing from @types/google.maps@3.55.2
          headerDisabled: true
        } as google.maps.InfoWindowOptions
      }
    >
      <Box width={280} data-testid="mixed-ride-segment-popup" bgcolor="#FFFFFF">
        <Box
          px={2}
          pt={1.5}
          pb={0.5}
          display="grid"
          gridTemplateColumns="14px minmax(0, 1fr)"
          columnGap={1.5}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <SplitDot />
            <Box
              flex={1}
              width="2px"
              bgcolor={color}
              my="3px"
              borderRadius={1}
              minHeight={16}
            />
            <SplitDot />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            minHeight={84}
            minWidth={0}
          >
            <EndpointCopy endpoint={start} />
            <EndpointCopy endpoint={end} />
          </Box>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mt={1.5}
          px={2}
          py={1.25}
          bgcolor={isPrivate ? "#F4F5F7" : "#E7F8F6"}
        >
          <Typography fontSize={12} fontWeight={700} color={color}>
            {isPrivate ? t("typography.private") : t("typography.business")}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <MileageIcon />
              <Typography fontSize={12} color="#323946">
                {getSegmentDistanceKm(segment)} km
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Clock />
              <Typography fontSize={12} color="#323946">
                {getSegmentDurationMinutes(segment)} min
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </InfoWindowF>
  );
};

export default MixedRideSegmentPopup;
